import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { Trash2 } from 'lucide-react';
import ImageUploadField from './ImageUploadField';
import FocalPointPicker from './FocalPointPicker';

const CrystalDifferencePanel = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editingSection, setEditingSection] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const data = await api.get('/api/content/crystal-difference', { skipAuth: true });
      // Ensure stats array exists
      if (!data.stats) {
        data.stats = [
          { value: '14', suffix: '+', label: 'Years' },
          { value: '25', suffix: '+', label: 'Specialists' },
          { value: '100', suffix: '%', label: 'Completion' },
          { value: '100', suffix: '%', label: 'Satisfaction' },
        ];
      }
      setContent(data);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      await api.put('/api/content/crystal-difference', content);
      setMessage('Saved successfully!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (index, file) => {
    setUploading(true);
    try {
      const result = await api.uploadImage(file, 'crystal-difference');
      const updated = { ...content };
      updated.sections[index].image_url = result.url;
      setContent(updated);
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const updateSection = (index, field, value) => {
    const updated = { ...content };
    updated.sections[index][field] = value;
    setContent(updated);
  };

  const updateStat = (index, field, value) => {
    const updated = { ...content };
    updated.stats[index][field] = value;
    setContent(updated);
  };

  const addStat = () => {
    const updated = { ...content };
    updated.stats.push({ value: '0', suffix: '+', label: 'New Stat' });
    setContent(updated);
  };

  const removeStat = (index) => {
    const updated = { ...content };
    updated.stats.splice(index, 1);
    setContent(updated);
  };

  const updateDeliverable = (index, value) => {
    const updated = { ...content };
    updated.deliverables[index] = value;
    setContent(updated);
  };

  const addDeliverable = () => {
    const updated = { ...content };
    updated.deliverables.push('New deliverable');
    setContent(updated);
  };

  const removeDeliverable = (index) => {
    const updated = { ...content };
    updated.deliverables.splice(index, 1);
    setContent(updated);
  };

  if (loading) return <div className="py-12 text-center text-gray-500">Loading...</div>;
  if (!content) return <div className="py-12 text-center text-gray-500">Failed to load content.</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Crystal Difference</h2>
          <p className="text-sm text-gray-500 mt-1">Manage the "Why Choose Us" parallax page content</p>
        </div>
        <button onClick={save} disabled={saving} className="px-6 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors disabled:opacity-50">
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>

      {message && (
        <div className={`p-3 rounded text-sm ${message.includes('success') || message.includes('Saved') ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          {message}
        </div>
      )}

      {/* Hero Section */}
      <div className="border border-gray-200 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold">Hero Section</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Subtitle</label>
            <input type="text" value={content.hero_subtitle} onChange={(e) => setContent({ ...content, hero_subtitle: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input type="text" value={content.hero_title} onChange={(e) => setContent({ ...content, hero_title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea value={content.hero_description} onChange={(e) => setContent({ ...content, hero_description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black" />
        </div>

        {/* Hero Background Image */}
        <ImageUploadField
          value={content.hero_bg_image_url || ''}
          onChange={(url) => setContent({ ...content, hero_bg_image_url: url })}
          folder="crystal-difference-hero"
          label="Hero Background Image"
        />

        {/* Overlay controls */}
        {content.hero_bg_image_url && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Overlay Color</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={content.hero_overlay_color || '#000000'} onChange={(e) => setContent({ ...content, hero_overlay_color: e.target.value })} className="w-10 h-10 border rounded cursor-pointer" />
                  <input type="text" value={content.hero_overlay_color || '#000000'} onChange={(e) => setContent({ ...content, hero_overlay_color: e.target.value })} className="flex-1 px-3 py-2 border rounded text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Overlay Opacity ({Math.round((content.hero_overlay_opacity || 0.3) * 100)}%)</label>
                <input type="range" min="0" max="1" step="0.05" value={content.hero_overlay_opacity || 0.3} onChange={(e) => setContent({ ...content, hero_overlay_opacity: parseFloat(e.target.value) })} className="w-full mt-2" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FocalPointPicker
                imageUrl={content.hero_bg_image_url}
                focalPoint={content.hero_bg_focal_desktop || { x: 50, y: 50 }}
                onChange={(fp) => setContent({ ...content, hero_bg_focal_desktop: fp })}
                label="Desktop Focus Point"
              />
              <FocalPointPicker
                imageUrl={content.hero_bg_image_url}
                focalPoint={content.hero_bg_focal_mobile || { x: 50, y: 50 }}
                onChange={(fp) => setContent({ ...content, hero_bg_focal_mobile: fp })}
                label="Mobile Focus Point"
              />
            </div>
          </>
        )}
      </div>

      {/* Stats Section */}
      <div className="border border-gray-200 rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Stats</h3>
          <button onClick={addStat} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors">+ Add Stat</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(content.stats || []).map((stat, i) => (
            <div key={i} className="flex items-center gap-2 p-3 bg-gray-50 rounded">
              <input type="text" value={stat.value} onChange={(e) => updateStat(i, 'value', e.target.value)} className="w-16 px-2 py-1 border rounded text-sm text-center" placeholder="14" />
              <input type="text" value={stat.suffix} onChange={(e) => updateStat(i, 'suffix', e.target.value)} className="w-10 px-2 py-1 border rounded text-sm text-center" placeholder="+" />
              <input type="text" value={stat.label} onChange={(e) => updateStat(i, 'label', e.target.value)} className="flex-1 px-2 py-1 border rounded text-sm" placeholder="Years" />
              <button onClick={() => removeStat(i)} className="p-1 text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Content Sections ({content.sections.length})</h3>
        {content.sections.map((section, i) => (
          <div key={section.id || i} className="border border-gray-200 rounded-lg overflow-hidden">
            <button onClick={() => setEditingSection(editingSection === i ? null : i)} className="w-full px-6 py-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors text-left">
              <div className="flex items-center gap-3">
                <span className="text-lg font-mono text-gray-400">{section.number}</span>
                <span className="font-medium text-gray-800 truncate max-w-md">{section.headline || 'Untitled section'}</span>
              </div>
              <div className="flex items-center gap-3">
                {section.image_url && <img src={section.image_url} alt="" className="w-8 h-8 object-contain rounded" />}
                <span className="text-gray-400 text-sm">{editingSection === i ? 'Collapse' : 'Edit'}</span>
              </div>
            </button>

            {editingSection === i && (
              <div className="px-6 py-4 space-y-4 border-t border-gray-200">
                {/* Image */}
                <div>
                  <label className="block text-sm font-medium mb-2">Section Image / GIF</label>
                  <div className="flex items-start gap-4">
                    {section.image_url && (
                      <div className="w-32 h-32 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                        <img src={section.image_url} alt="" className="w-full h-full object-contain" />
                      </div>
                    )}
                    <div className="flex-1 space-y-2">
                      <input type="text" value={section.image_url} onChange={(e) => updateSection(i, 'image_url', e.target.value)} placeholder="Image URL" className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-black" />
                      <div>
                        <label className="inline-block px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded cursor-pointer transition-colors">
                          {uploading ? 'Uploading...' : 'Upload Image'}
                          <input type="file" accept="image/*,.gif" className="hidden" onChange={(e) => { if (e.target.files[0]) handleImageUpload(i, e.target.files[0]); }} disabled={uploading} />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Image Focal Points */}
                {section.image_url && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FocalPointPicker
                      imageUrl={section.image_url}
                      focalPoint={section.image_focal_desktop || { x: 50, y: 50 }}
                      onChange={(fp) => updateSection(i, 'image_focal_desktop', fp)}
                      label="Desktop Focus Point"
                    />
                    <FocalPointPicker
                      imageUrl={section.image_url}
                      focalPoint={section.image_focal_mobile || { x: 50, y: 50 }}
                      onChange={(fp) => updateSection(i, 'image_focal_mobile', fp)}
                      label="Mobile Focus Point"
                    />
                  </div>
                )}

                {/* Text Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Section Number</label>
                    <input type="text" value={section.number} onChange={(e) => updateSection(i, 'number', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Order</label>
                    <input type="number" value={section.order} onChange={(e) => updateSection(i, 'order', parseInt(e.target.value) || 0)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Headline</label>
                  <input type="text" value={section.headline} onChange={(e) => updateSection(i, 'headline', e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Body</label>
                  <textarea value={section.body} onChange={(e) => updateSection(i, 'body', e.target.value)} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Sub Body (optional)</label>
                  <textarea value={section.sub_body || ''} onChange={(e) => updateSection(i, 'sub_body', e.target.value || null)} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Deliverables */}
      <div className="border border-gray-200 rounded-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Deliverables ("What you get")</h3>
          <button onClick={addDeliverable} className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors">+ Add</button>
        </div>
        {content.deliverables.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-sm text-gray-400 w-6">{i + 1}.</span>
            <input type="text" value={item} onChange={(e) => updateDeliverable(i, e.target.value)} className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-black" />
            <button onClick={() => removeDeliverable(i)} className="px-2 py-1 text-sm text-red-500 hover:bg-red-50 rounded">Remove</button>
          </div>
        ))}
      </div>

      {/* CTA Section */}
      <div className="border border-gray-200 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold">CTA Section</h3>
        <div>
          <label className="block text-sm font-medium mb-1">Heading</label>
          <input type="text" value={content.cta_heading} onChange={(e) => setContent({ ...content, cta_heading: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Description</label>
          <textarea value={content.cta_description} onChange={(e) => setContent({ ...content, cta_description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-black" />
        </div>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="px-8 py-3 bg-black text-white rounded hover:bg-gray-800 transition-colors disabled:opacity-50">
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>
    </div>
  );
};

export default CrystalDifferencePanel;
